import React, { ReactElement, useRef } from "react";
import { useTranslation } from "react-i18next";

import { I18N_NAMESPACES } from "../../../helpers/constants";
import { RewardCollections_rewardCollections } from "../../../types/__generated__/RewardCollections";
import { RewardStoreItemFragment } from "../../../types/__generated__/RewardStoreItemFragment";
import urlPatterns from "../../../urls";
import ArrowNavigator from "../../atoms/ArrowNavigator";
import Tooltip from "../../atoms/Tooltip";
import {
  Gallery,
  GalleryHeader,
  GalleryHeading,
  GalleryTitle,
  GalleryActions,
  SeeAll,
  GalleryItems,
} from "./style";

interface RewardCollectionGalleryProps {
  collection: RewardCollections_rewardCollections;
  renderReward: (reward: RewardStoreItemFragment) => ReactElement | null;
}

const RewardCollectionGallery = ({ collection, renderReward }: RewardCollectionGalleryProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const horizontalContainer = useRef<HTMLDivElement>(null);
  const items = collection.items?.filter(
    (item) => item?.product.isRedeemableWithPoints && item.product.pointCost !== null
  );
  if (!collection.slug || !items?.length) return null;

  return (
    <Gallery aria-label={collection.name || undefined}>
      <GalleryHeader>
        <GalleryHeading>
          <GalleryTitle>{collection.name}</GalleryTitle>
          {/* Like the wine galleries, the description lives in the "?" tooltip beside the title. */}
          {collection.description && <Tooltip tootipTitle={collection.description} />}
        </GalleryHeading>
        <GalleryActions>
          <SeeAll to={urlPatterns.REWARD_COLLECTION(collection.slug)}>{t("common:seeAll")}</SeeAll>
          <ArrowNavigator horizontalContainer={horizontalContainer} scrollAdjustment={20} />
        </GalleryActions>
      </GalleryHeader>
      <GalleryItems ref={horizontalContainer}>
        {items.slice(0, 8).map((item) => item && renderReward(item))}
      </GalleryItems>
    </Gallery>
  );
};

export default RewardCollectionGallery;
